import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Building2, UserCog, CalendarClock } from 'lucide-react';
import { CrmShell } from './CrmShell';
import { Badge, Avatar, ProgressBar, PropRow, EmptyState } from './ui';
import {
  dealById, companyById, contactById, contactFullName,
  STAGES, STAGE_TONE, stageByKey, stageIndex, formatEur, formatDateFr,
} from './mockData';

export default function CrmDeal({ plugins }) {
  const { id } = useParams();
  const deal = dealById(id);

  if (!deal) {
    return (
      <CrmShell plugins={plugins}>
        <EmptyState title="Transaction introuvable" message="Cette transaction de démonstration n'existe pas.">
          <Link to="/crm" className="btn btn-secondary">Retour au CRM</Link>
        </EmptyState>
      </CrmShell>
    );
  }

  const company = companyById(deal.companyId);
  const contact = contactById(deal.contactId);
  const stg = stageByKey(deal.stage);
  const current = stageIndex(deal.stage);

  return (
    <CrmShell plugins={plugins}>
      <Link to="/crm" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-iris-700 transition-colors mb-4">
        <ArrowLeft size={16} /> CRM
      </Link>

      {/* En-tête */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="h-page">{deal.name}</h1>
              <Badge tone={STAGE_TONE[deal.stage]} dot>{stg?.title}</Badge>
            </div>
            {company && (
              <Link to={`/crm/societe/${company.id}`} className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-iris-700 hover:underline">
                <Building2 size={14} /> {company.emoji} {company.name}
              </Link>
            )}
          </div>
          <div className="text-right">
            <span className="block font-display text-3xl font-bold text-slate-900 tabular-nums tracking-tight">{formatEur(deal.amount)}</span>
            <span className="block text-xs text-slate-400 mt-0.5">Montant de la transaction</span>
          </div>
        </div>

        {/* Stepper d'étapes */}
        <ol className="mt-6 flex items-center gap-1.5">
          {STAGES.map((s, i) => {
            const done = i < current;
            const here = i === current;
            return (
              <li key={s.key} className="flex-1 flex items-center gap-1.5">
                <span className="flex flex-col items-center gap-1.5 flex-none">
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-2xs font-bold flex-none border-2 ${
                      done ? 'bg-iris-600 border-iris-600 text-white'
                      : here ? 'bg-white border-iris-500 text-iris-700'
                      : 'bg-white border-slate-200 text-slate-400'
                    }`}
                    aria-hidden="true"
                  >
                    {done ? <Check size={14} /> : i + 1}
                  </span>
                </span>
                {i < STAGES.length - 1 && (
                  <span className={`flex-1 h-0.5 rounded-full ${i < current ? 'bg-iris-500' : 'bg-slate-200'}`} aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
        <div className="mt-2 flex items-center justify-between text-2xs font-semibold uppercase tracking-wide text-slate-400">
          {STAGES.map((s) => <span key={s.key} className="flex-1 text-center first:text-left last:text-right">{s.title}</span>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-semibold text-slate-900 mb-2">Description</h2>
            <p className="text-sm text-slate-700 leading-relaxed">{deal.details}</p>
          </section>
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">Probabilité de signature</h2>
              <span className="font-display text-lg font-bold text-slate-900 tabular-nums">{deal.probability} %</span>
            </div>
            <ProgressBar value={deal.probability} tone={deal.probability >= 80 ? 'success' : deal.probability >= 50 ? 'primary' : 'warning'} />
          </section>
        </div>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col gap-4">
          <h2 className="font-semibold text-slate-900">Détails</h2>
          {company && (
            <PropRow label="Société">
              <Link to={`/crm/societe/${company.id}`} className="inline-flex items-center gap-1.5 text-iris-700 hover:underline">
                <Building2 size={14} /> {company.name}
              </Link>
            </PropRow>
          )}
          {contact && (
            <PropRow label="Contact">
              <Link to={`/crm/contact/${contact.id}`} className="inline-flex items-center gap-2 hover:underline text-slate-700">
                <Avatar name={contactFullName(contact)} size="sm" /> {contactFullName(contact)}
              </Link>
            </PropRow>
          )}
          <PropRow label="Responsable"><span className="inline-flex items-center gap-1.5"><UserCog size={14} className="text-slate-400" /> {deal.owner}</span></PropRow>
          <PropRow label="Échéance"><span className="inline-flex items-center gap-1.5"><CalendarClock size={14} className="text-slate-400" /> {formatDateFr(deal.dueDate)}</span></PropRow>
          <PropRow label="Étape"><Badge tone={STAGE_TONE[deal.stage]}>{stg?.title}</Badge></PropRow>
        </section>
      </div>
    </CrmShell>
  );
}
